import { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Edit, 
  Trash2, 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle,
  CheckCircle2,
  Clock,
  DollarSign,
  Users,
  BarChart3,
  Lightbulb,
  AlertCircle,
  Target,
  Activity,
  Maximize2,
} from 'lucide-react';
import { formatCurrency, formatHours, formatPercent, getProgressColor } from '@/lib/budget-utils';
import { cn } from '@/lib/utils';
import type { CostCentre } from './cost-centres-table';

interface CostCentreDetailsPanelProps {
  costCentre: CostCentre | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit?: (costCentre: CostCentre) => void;
  onDelete?: (costCentreId: string) => void;
  onMarkInactive?: (costCentreId: string) => void;
  onExpandToFullView?: () => void;
}

interface Transaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  category: string;
}

interface CostBreakdown {
  labour: number;
  expenses: number;
  materials: number;
  subcontractors: number;
}

export function CostCentreDetailsPanel({
  costCentre,
  open,
  onOpenChange,
  onEdit,
  onDelete,
  onMarkInactive,
  onExpandToFullView,
}: CostCentreDetailsPanelProps) {
  const [activeTab, setActiveTab] = useState('details');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteDialogType, setDeleteDialogType] = useState<'has-transactions' | 'has-budget' | 'confirm'>('confirm');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState<CostCentre | null>(null);
  const [isActive, setIsActive] = useState(true);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Reset edit mode when panel closes or cost centre changes
  useEffect(() => {
    if (!open) {
      setIsEditMode(false);
      setIsActive(costCentre?.isActive ?? true);
      setEditFormData(null);
    }
  }, [open, costCentre?.isActive]);

  useEffect(() => {
    if (costCentre) {
      setIsEditMode(false);
      setEditFormData(null);
    }
  }, [costCentre?.id]);

  // Reset edit mode when switching tabs
  useEffect(() => {
    if (activeTab !== 'details') {
      setIsEditMode(false);
      setEditFormData(null);
    }
  }, [activeTab]);

  if (!costCentre) return null;

  // Handle entering edit mode
  const handleEnterEditMode = () => {
    setEditFormData({ ...costCentre });
    setIsEditMode(true);
  };

  // Handle canceling edit mode
  const handleCancelEdit = () => {
    setEditFormData(null);
    setIsEditMode(false);
  };

  // Handle saving edits
  const handleSaveEdit = () => {
    if (editFormData) {
      onEdit?.(editFormData);
      setIsEditMode(false);
      setEditFormData(null);
    }
  };

  // Handle status toggle
  const handleStatusToggle = async (checked: boolean) => {
    if (!costCentre) return;
    
    setIsUpdatingStatus(true);
    const statusText = checked ? 'active' : 'inactive';
    
    try {
      // Optimistic update
      setIsActive(checked);
      
      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Update the cost centre (you may need to call a parent handler here)
      // For now, just show success toast
      toast.success(`Cost centre marked as ${statusText}`, {
        duration: 3000,
      });
    } catch (error) {
      // Revert on error
      setIsActive(!checked);
      toast.error('Failed to update status. Please try again.');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Update form field
  const updateEditField = (field: keyof CostCentre, value: any) => {
    if (editFormData) {
      setEditFormData({ ...editFormData, [field]: value });
    }
  };

  // Check if cost centre has been used (has any actual costs, hours, or revenue)
  const isCostCentreUsed = (cc: CostCentre | null): boolean => {
    if (!cc) return false;
    return (cc.actualCosts > 0 || cc.actualHours > 0 || cc.actualRevenue > 0);
  };

  // Calculate metrics
  const totalCosts = costCentre.actualCosts + costCentre.committedCosts;
  const costVariance = costCentre.costsBudget - costCentre.actualCosts;
  const costPercentUsed = costCentre.costsBudget > 0 
    ? (costCentre.actualCosts / costCentre.costsBudget) * 100 
    : 0;
  const hoursVariance = costCentre.hoursBudget - costCentre.actualHours;
  const hoursPercentUsed = costCentre.hoursBudget > 0
    ? (costCentre.actualHours / costCentre.hoursBudget) * 100
    : 0;
  const remainingHours = costCentre.hoursBudget - costCentre.actualHours;
  
  // Revenue metrics
  const revenueVariance = costCentre.actualRevenue - costCentre.revenueBudget;
  const revenuePercentAchieved = costCentre.revenueBudget > 0 
    ? (costCentre.actualRevenue / costCentre.revenueBudget) * 100 
    : 0;
  const remainingRevenue = costCentre.revenueBudget - costCentre.actualRevenue;
  const profitMargin = costCentre.actualRevenue > 0
    ? ((costCentre.actualRevenue - costCentre.actualCosts) / costCentre.actualRevenue) * 100
    : 0;
  
  // Check if cost centre has any actuals (transactions)
  const hasTransactions = costCentre.actualCosts > 0 || costCentre.actualHours > 0 || costCentre.actualRevenue > 0;
  
  // Check if cost centre has any budgets
  const hasBudget = costCentre.costsBudget > 0 || costCentre.hoursBudget > 0 || costCentre.revenueBudget > 0;

  // Mock cost breakdown (in real app, this would come from API)
  const costBreakdown: CostBreakdown = {
    labour: costCentre.actualCosts * 0.6,
    expenses: costCentre.actualCosts * 0.15,
    materials: costCentre.actualCosts * 0.15,
    subcontractors: costCentre.actualCosts * 0.1,
  };

  // Mock transactions (in real app, this would come from API)
  const recentTransactions: Transaction[] = [
    { id: '1', date: '2024-01-15', description: 'Developer salary - January', amount: 5000, category: 'Labour' },
    { id: '2', date: '2024-01-14', description: 'Software licenses', amount: 1200, category: 'Expenses' },
    { id: '3', date: '2024-01-12', description: 'Cloud hosting services', amount: 800, category: 'Infrastructure' },
    { id: '4', date: '2024-01-10', description: 'Design tools subscription', amount: 450, category: 'Expenses' },
    { id: '5', date: '2024-01-08', description: 'Contractor payment', amount: 3500, category: 'Subcontractors' },
    { id: '6', date: '2024-01-05', description: 'Office supplies', amount: 250, category: 'Materials' },
    { id: '7', date: '2024-01-03', description: 'Training materials', amount: 600, category: 'Expenses' },
    { id: '8', date: '2024-01-02', description: 'Equipment purchase', amount: 2100, category: 'Materials' },
    { id: '9', date: '2023-12-28', description: 'Consultant fees', amount: 4200, category: 'Subcontractors' },
    { id: '10', date: '2023-12-20', description: 'Team building event', amount: 850, category: 'Expenses' },
  ];

  // Calculate burn rate (simplified - costs per month)
  const monthsElapsed = 3; // Mock value
  const burnRate = costCentre.actualCosts / monthsElapsed;

  // Budget status color
  const getBudgetStatusColor = (percent: number) => {
    if (percent < 75) return 'text-green-600 dark:text-green-500';
    if (percent < 90) return 'text-amber-600 dark:text-amber-500';
    return 'text-red-600 dark:text-red-500';
  };

  const getBudgetStatusBg = (percent: number) => {
    if (percent < 75) return 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800';
    if (percent < 90) return 'bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-800';
    return 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800';
  };

  const handleDeleteClick = () => {
    // Tier 1: Has transactions
    if (hasTransactions) {
      setDeleteDialogType('has-transactions');
      setDeleteDialogOpen(true);
      return;
    }

    // Tier 2: Has budget but no transactions
    if (hasBudget) {
      setDeleteDialogType('has-budget');
      setDeleteDialogOpen(true);
      return;
    }

    // Tier 3: No transactions and no budget
    setDeleteDialogType('confirm');
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (onDelete) {
      onDelete(costCentre.id);
      setDeleteDialogOpen(false);
      onOpenChange(false);
    }
  };

  const handleMarkInactive = () => {
    if (onMarkInactive) {
      onMarkInactive(costCentre.id);
      setDeleteDialogOpen(false);
      onOpenChange(false);
    }
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-2xl p-0 flex flex-col h-full">
          {/* Fixed Header */}
          <div className="border-b bg-background p-6 pb-4 flex-shrink-0">
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1 flex-1">
                  <SheetTitle className="text-2xl font-bold">{costCentre.name}</SheetTitle>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="font-mono font-medium">{costCentre.code}</span>
                    {costCentre.type && (
                      <>
                        <span>•</span>
                        <span>{costCentre.type}</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Status Toggle */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={isActive}
                          onCheckedChange={handleStatusToggle}
                          disabled={isUpdatingStatus}
                          aria-label="Toggle cost centre status"
                        />
                        <span className={cn(
                          "text-sm font-medium transition-colors whitespace-nowrap",
                          isActive ? "text-green-600 dark:text-green-500" : "text-muted-foreground"
                        )}>
                          {isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Toggle cost centre status</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              
              {/* Expand to Full View Button */}
              {onExpandToFullView && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onExpandToFullView}
                  className="w-full"
                >
                  <Maximize2 className="h-4 w-4 mr-2" />
                  Expand to Full View
                </Button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
            <TabsList className="w-full justify-start rounded-none border-b px-6 flex-shrink-0">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="performance">Performance</TabsTrigger>
              <TabsTrigger value="insights">Insights</TabsTrigger>
            </TabsList>

            <ScrollArea className="flex-1 min-h-0">
              <ScrollAreaViewport className="h-full max-h-full">
                {/* Tab 1: Details */}
                <TabsContent value="details" className="p-6 space-y-6 mt-0">
                {/* Edit Mode Indicator */}
                {isEditMode && (
                  <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-center gap-2">
                    <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                      Edit Mode - Make your changes and click Save Changes
                    </span>
                  </div>
                )}

                {/* Action Buttons */}
                {!isEditMode ? (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleEnterEditMode}
                      className="flex-1"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDeleteClick}
                      className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCancelEdit}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSaveEdit}
                      className="flex-1"
                    >
                      Save Changes
                    </Button>
                  </div>
                )}

                <Separator />

                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Basic Information</h3>
                  {!isEditMode ? (
                    <div className="grid gap-4">
                      <div className="grid grid-cols-3 gap-2">
                        <span className="text-sm text-muted-foreground">Code</span>
                        <span className="col-span-2 text-sm font-mono font-medium">{costCentre.code}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <span className="text-sm text-muted-foreground">Name</span>
                        <span className="col-span-2 text-sm font-medium">{costCentre.name}</span>
                      </div>
                      {costCentre.description && (
                        <div className="grid grid-cols-3 gap-2">
                          <span className="text-sm text-muted-foreground">Description</span>
                          <span className="col-span-2 text-sm">{costCentre.description}</span>
                        </div>
                      )}
                      {costCentre.type && (
                        <div className="grid grid-cols-3 gap-2">
                          <span className="text-sm text-muted-foreground">Type</span>
                          <span className="col-span-2 text-sm">
                            <Badge variant="secondary">{costCentre.type}</Badge>
                          </span>
                        </div>
                      )}
                      {costCentre.status && (
                        <div className="grid grid-cols-3 gap-2">
                          <span className="text-sm text-muted-foreground">Status</span>
                          <span className="col-span-2 text-sm">
                            <Badge 
                              variant={
                                costCentre.status === 'In Progress' ? 'default' : 
                                costCentre.status === 'Completed' ? 'default' : 
                                'secondary'
                              }
                              className={
                                costCentre.status === 'In Progress' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' :
                                costCentre.status === 'Completed' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                                costCentre.status === 'On Hold' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' :
                                costCentre.status === 'Cancelled' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' :
                                'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                              }
                            >
                              {costCentre.status}
                            </Badge>
                          </span>
                        </div>
                      )}
                      <div className="grid grid-cols-3 gap-2">
                        <span className="text-sm text-muted-foreground">Billable</span>
                        <span className="col-span-2 text-sm">
                          {costCentre.billable ? (
                            <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Yes
                            </Badge>
                          ) : (
                            <Badge variant="secondary">No</Badge>
                          )}
                        </span>
                      </div>
                      {costCentre.owner && (
                        <div className="grid grid-cols-3 gap-2">
                          <span className="text-sm text-muted-foreground">Owner</span>
                          <span className="col-span-2 text-sm">{costCentre.owner}</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="grid gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="edit-code">Code</Label>
                        <Input
                          id="edit-code"
                          value={editFormData?.code || ''}
                          onChange={(e) => updateEditField('code', e.target.value)}
                          className="font-mono"
                          disabled={isCostCentreUsed(costCentre)}
                        />
                        {isCostCentreUsed(costCentre) && (
                          <p className="text-xs text-muted-foreground">
                            Code cannot be changed once the cost centre has been used
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-name">Name</Label>
                        <Input
                          id="edit-name"
                          value={editFormData?.name || ''}
                          onChange={(e) => updateEditField('name', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-description">Description</Label>
                        <Textarea
                          id="edit-description"
                          value={editFormData?.description || ''}
                          onChange={(e) => updateEditField('description', e.target.value)}
                          rows={3}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-type">Type</Label>
                        <Input
                          id="edit-type"
                          value={editFormData?.type || ''}
                          onChange={(e) => updateEditField('type', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-status">Status</Label>
                        <Select
                          value={editFormData?.status || 'Not Started'}
                          onValueChange={(value) => updateEditField('status', value)}
                        >
                          <SelectTrigger id="edit-status">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Not Started">Not Started</SelectItem>
                            <SelectItem value="In Progress">In Progress</SelectItem>
                            <SelectItem value="On Hold">On Hold</SelectItem>
                            <SelectItem value="Completed">Completed</SelectItem>
                            <SelectItem value="Cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-billable">Billable</Label>
                        <Select
                          value={editFormData?.billable ? 'yes' : 'no'}
                          onValueChange={(value) => updateEditField('billable', value === 'yes')}
                        >
                          <SelectTrigger id="edit-billable">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="yes">Yes</SelectItem>
                            <SelectItem value="no">No</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-owner">Owner</Label>
                        <Input
                          id="edit-owner"
                          value={editFormData?.owner || ''}
                          onChange={(e) => updateEditField('owner', e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Budget Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Budget Information</h3>
                  {!isEditMode ? (
                    <div className="grid gap-4">
                      <div className="grid grid-cols-3 gap-2">
                        <span className="text-sm text-muted-foreground">Budget Amount</span>
                        <span className="col-span-2 text-sm font-semibold">{formatCurrency(costCentre.costsBudget)}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <span className="text-sm text-muted-foreground">Budget Hours</span>
                        <span className="col-span-2 text-sm font-semibold">{formatHours(costCentre.hoursBudget)}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <span className="text-sm text-muted-foreground">Revenue Budget</span>
                        <span className="col-span-2 text-sm font-semibold">{formatCurrency(costCentre.revenueBudget)}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="grid gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="edit-costs-budget">Budget Amount</Label>
                        <Input
                          id="edit-costs-budget"
                          type="number"
                          step="0.01"
                          value={editFormData?.costsBudget || 0}
                          onChange={(e) => updateEditField('costsBudget', parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-hours-budget">Budget Hours</Label>
                        <Input
                          id="edit-hours-budget"
                          type="number"
                          step="0.5"
                          value={editFormData?.hoursBudget || 0}
                          onChange={(e) => updateEditField('hoursBudget', parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-revenue-budget">Revenue Budget</Label>
                        <Input
                          id="edit-revenue-budget"
                          type="number"
                          step="0.01"
                          value={editFormData?.revenueBudget || 0}
                          onChange={(e) => updateEditField('revenueBudget', parseFloat(e.target.value) || 0)}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Timestamps */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Audit Information</h3>
                  <div className="grid gap-4">
                    {costCentre.createdAt && (
                      <div className="grid grid-cols-3 gap-2">
                        <span className="text-sm text-muted-foreground">Created</span>
                        <span className="col-span-2 text-sm">{formatDate(costCentre.createdAt)}</span>
                      </div>
                    )}
                    {costCentre.updatedAt && (
                      <div className="grid grid-cols-3 gap-2">
                        <span className="text-sm text-muted-foreground">Last Updated</span>
                        <span className="col-span-2 text-sm">{formatDate(costCentre.updatedAt)}</span>
                      </div>
                    )}
                    {!costCentre.createdAt && !costCentre.updatedAt && costCentre.lastUpdated && (
                      <div className="grid grid-cols-3 gap-2">
                        <span className="text-sm text-muted-foreground">Last Updated</span>
                        <span className="col-span-2 text-sm">{formatDate(costCentre.lastUpdated)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Custom Fields */}
                {costCentre.customFields && Object.keys(costCentre.customFields).length > 0 && (
                  <>
                    <Separator />
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">Additional Information</h3>
                      <div className="grid gap-4">
                        {Object.entries(costCentre.customFields).map(([key, value]) => (
                          <div key={key} className="grid grid-cols-3 gap-2">
                            <span className="text-sm text-muted-foreground">{key}</span>
                            <span className="col-span-2 text-sm">
                              {typeof value === 'boolean' ? (
                                value ? (
                                  <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                    Yes
                                  </Badge>
                                ) : (
                                  <Badge variant="secondary">No</Badge>
                                )
                              ) : value instanceof Date ? (
                                formatDate(value)
                              ) : typeof value === 'number' ? (
                                // Check if it looks like a currency value (has decimals or is large)
                                key.toLowerCase().includes('amount') || 
                                key.toLowerCase().includes('budget') || 
                                key.toLowerCase().includes('cost') || 
                                key.toLowerCase().includes('threshold') ? (
                                  formatCurrency(value)
                                ) : (
                                  value.toLocaleString()
                                )
                              ) : (
                                String(value)
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
                </TabsContent>

                {/* Tab 2: Performance */}
                <TabsContent value="performance" className="p-4 space-y-4 mt-0">
                  {/* Financial Overview - 3 Column Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {/* Costs Card */}
                    <div className="rounded-lg border p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-medium text-muted-foreground uppercase">Costs</h4>
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-baseline justify-between">
                          <span className="text-xs text-muted-foreground">Budget</span>
                          <span className="text-sm font-semibold">{formatCurrency(costCentre.costsBudget)}</span>
                        </div>
                        <div className="flex items-baseline justify-between">
                          <span className="text-xs text-muted-foreground">Actual</span>
                          <span className="text-sm font-semibold">{formatCurrency(costCentre.actualCosts)}</span>
                        </div>
                      </div>
                      <Progress value={Math.min(costPercentUsed, 100)} className="h-1.5" />
                      <div className="flex items-center justify-between pt-1">
                        <span className={cn("text-xs font-medium", getBudgetStatusColor(costPercentUsed))}>
                          {formatPercent(costPercentUsed, 0)}
                        </span>
                        <Badge variant="secondary" className={cn("text-xs py-0 h-5",
                          costPercentUsed < 75 ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                          costPercentUsed < 90 ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300' :
                          'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                        )}>
                          {costPercentUsed < 75 ? 'On Track' : costPercentUsed < 90 ? 'At Risk' : 'Over'}
                        </Badge>
                      </div>
                    </div>

                    {/* Revenue Card */}
                    <div className="rounded-lg border p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-medium text-muted-foreground uppercase">Revenue</h4>
                        <TrendingUp className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-baseline justify-between">
                          <span className="text-xs text-muted-foreground">Budget</span>
                          <span className="text-sm font-semibold">{formatCurrency(costCentre.revenueBudget)}</span>
                        </div>
                        <div className="flex items-baseline justify-between">
                          <span className="text-xs text-muted-foreground">Actual</span>
                          <span className="text-sm font-semibold">{formatCurrency(costCentre.actualRevenue)}</span>
                        </div>
                      </div>
                      <Progress value={Math.min(revenuePercentAchieved, 100)} className="h-1.5" />
                      <div className="flex items-center justify-between pt-1">
                        <span className={cn("text-xs font-medium", 
                          revenuePercentAchieved >= 90 ? 'text-green-600 dark:text-green-500' :
                          revenuePercentAchieved >= 75 ? 'text-amber-600 dark:text-amber-500' :
                          'text-red-600 dark:text-red-500'
                        )}>
                          {formatPercent(revenuePercentAchieved, 0)}
                        </span>
                        <Badge variant="secondary" className={cn("text-xs py-0 h-5",
                          revenuePercentAchieved >= 90 ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                          revenuePercentAchieved >= 75 ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300' :
                          'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                        )}>
                          {revenuePercentAchieved >= 90 ? 'Excellent' : revenuePercentAchieved >= 75 ? 'Good' : 'Below'}
                        </Badge>
                      </div>
                    </div>

                    {/* Hours Card */}
                    <div className="rounded-lg border p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-medium text-muted-foreground uppercase">Hours</h4>
                        <Clock className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-baseline justify-between">
                          <span className="text-xs text-muted-foreground">Budget</span>
                          <span className="text-sm font-semibold">{costCentre.hoursBudget.toLocaleString()}h</span>
                        </div>
                        <div className="flex items-baseline justify-between">
                          <span className="text-xs text-muted-foreground">Actual</span>
                          <span className="text-sm font-semibold">{costCentre.actualHours.toLocaleString()}h</span>
                        </div>
                      </div>
                      <Progress value={Math.min(hoursPercentUsed, 100)} className="h-1.5" />
                      <div className="flex items-center justify-between pt-1">
                        <span className={cn("text-xs font-medium", getBudgetStatusColor(hoursPercentUsed))}>
                          {formatPercent(hoursPercentUsed, 0)}
                        </span>
                        <span className={cn("text-xs font-medium", remainingHours >= 0 ? 'text-green-600' : 'text-red-600')}>
                          {remainingHours >= 0 ? '+' : ''}{remainingHours.toLocaleString()}h
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Detailed Metrics - 2 Column Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Cost Details */}
                    <div className="rounded-lg border p-3 space-y-2">
                      <h4 className="text-sm font-semibold">Cost Details</h4>
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">Variance</span>
                          <span className={cn("font-medium", costVariance >= 0 ? 'text-green-600' : 'text-red-600')}>
                            {costVariance >= 0 ? '+' : ''}{formatCurrency(costVariance)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">Committed</span>
                          <span className="font-medium">{formatCurrency(costCentre.committedCosts)}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">Burn Rate</span>
                          <span className="font-medium">{formatCurrency(burnRate)}/mo</span>
                        </div>
                      </div>
                    </div>

                    {/* Revenue Details */}
                    <div className="rounded-lg border p-3 space-y-2">
                      <h4 className="text-sm font-semibold">Revenue Details</h4>
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">Variance</span>
                          <span className={cn("font-medium", revenueVariance >= 0 ? 'text-green-600' : 'text-red-600')}>
                            {revenueVariance >= 0 ? '+' : ''}{formatCurrency(revenueVariance)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">Remaining</span>
                          <span className="font-medium">{formatCurrency(Math.abs(remainingRevenue))}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">Profit Margin</span>
                          <span className={cn("font-medium", profitMargin >= 0 ? 'text-green-600' : 'text-red-600')}>
                            {formatPercent(profitMargin, 1)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Cost Breakdown - Compact Horizontal */}
                  <div className="rounded-lg border p-3 space-y-2">
                    <h4 className="text-sm font-semibold flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" />
                      Cost Breakdown
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {Object.entries(costBreakdown).map(([category, amount]) => {
                        const percentage = costCentre.actualCosts > 0 ? (amount / costCentre.actualCosts) * 100 : 0;
                        return (
                          <div key={category} className="space-y-1">
                            <div className="flex items-center justify-between text-xs">
                              <span className="font-medium capitalize">{category}</span>
                              <span className="text-muted-foreground">
                                {formatCurrency(amount)} ({formatPercent(percentage, 0)})
                              </span>
                            </div>
                            <Progress value={percentage} className="h-1" />
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Recent Transactions - Condensed */}
                  <div className="rounded-lg border">
                    <div className="flex items-center justify-between p-3 border-b">
                      <h4 className="text-sm font-semibold flex items-center gap-2">
                        <Activity className="h-4 w-4" />
                        Recent Transactions
                      </h4>
                      <Button variant="ghost" size="sm" className="h-7 text-xs" disabled>
                        View All →
                      </Button>
                    </div>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="hover:bg-transparent">
                            <TableHead className="h-8 text-xs">Date</TableHead>
                            <TableHead className="h-8 text-xs">Description</TableHead>
                            <TableHead className="h-8 text-xs">Category</TableHead>
                            <TableHead className="h-8 text-xs text-right">Amount</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {recentTransactions.slice(0, 5).map((transaction) => (
                            <TableRow key={transaction.id} className="h-9">
                              <TableCell className="text-xs py-1.5">
                                {new Date(transaction.date).toLocaleDateString('en-GB', {
                                  day: '2-digit',
                                  month: 'short',
                                })}
                              </TableCell>
                              <TableCell className="text-xs py-1.5">{transaction.description}</TableCell>
                              <TableCell className="py-1.5">
                                <Badge variant="secondary" className="text-xs py-0 h-4">{transaction.category}</Badge>
                              </TableCell>
                              <TableCell className="text-xs font-medium text-right py-1.5">
                                {formatCurrency(transaction.amount)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </TabsContent>

                {/* Tab 3: Insights */}
                <TabsContent value="insights" className="p-6 space-y-6 mt-0">
                {/* AI-Generated Insights */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Lightbulb className="h-5 w-5" />
                    Key Observations
                  </h3>
                  
                  <div className="space-y-3">
                    <div className="rounded-lg border p-4 space-y-2">
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                        <div>
                          <p className="font-medium">Budget Performance</p>
                          <p className="text-sm text-muted-foreground">
                            This cost centre is tracking {costPercentUsed < 100 ? 'within' : 'over'} budget at {formatPercent(costPercentUsed, 1)} utilization.
                            {costPercentUsed < 75 && ' Performance is excellent with significant budget remaining.'}
                            {costPercentUsed >= 75 && costPercentUsed < 90 && ' Monitor closely as budget approaches limit.'}
                            {costPercentUsed >= 90 && ' Immediate attention required to prevent budget overrun.'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-lg border p-4 space-y-2">
                      <div className="flex items-start gap-3">
                        <TrendingUp className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
                        <div>
                          <p className="font-medium">Resource Utilization</p>
                          <p className="text-sm text-muted-foreground">
                            Hours utilization is at {formatPercent(hoursPercentUsed, 1)} with {remainingHours.toLocaleString()} hours remaining.
                            Current burn rate suggests {remainingHours > 0 ? 'completion within budget' : 'additional hours may be needed'}.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-lg border p-4 space-y-2">
                      <div className="flex items-start gap-3">
                        <BarChart3 className="h-5 w-5 text-purple-600 mt-0.5 shrink-0" />
                        <div>
                          <p className="font-medium">Cost Distribution</p>
                          <p className="text-sm text-muted-foreground">
                            Labour costs represent {formatPercent((costBreakdown.labour / costCentre.actualCosts) * 100, 0)} of total spend,
                            which is typical for {costCentre.type || 'this type of'} cost centre.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Active Alerts */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <AlertCircle className="h-5 w-5" />
                    Active Alerts
                  </h3>
                  
                  {costPercentUsed >= 90 ? (
                    <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-950 dark:border-red-800 p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
                        <div>
                          <p className="font-medium text-red-900 dark:text-red-100">Budget Alert</p>
                          <p className="text-sm text-red-700 dark:text-red-300">
                            Cost centre has exceeded 90% of allocated budget. Review spending and consider budget reallocation.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : costPercentUsed >= 75 ? (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950 dark:border-amber-800 p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                        <div>
                          <p className="font-medium text-amber-900 dark:text-amber-100">Budget Warning</p>
                          <p className="text-sm text-amber-700 dark:text-amber-300">
                            Cost centre has reached 75% of allocated budget. Monitor spending closely.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-lg border p-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                        <div>
                          <p className="font-medium">No Active Alerts</p>
                          <p className="text-sm text-muted-foreground">
                            All metrics are within acceptable ranges. Continue monitoring performance.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Benchmarking */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    Comparison to Similar Cost Centres
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="rounded-lg border p-4 space-y-2">
                      <p className="text-sm text-muted-foreground">Your Performance</p>
                      <p className="text-2xl font-bold">{formatPercent(costPercentUsed, 0)}</p>
                      <p className="text-xs text-muted-foreground">Budget Utilization</p>
                    </div>
                    <div className="rounded-lg border p-4 space-y-2">
                      <p className="text-sm text-muted-foreground">Average</p>
                      <p className="text-2xl font-bold">78%</p>
                      <p className="text-xs text-muted-foreground">Similar Cost Centres</p>
                    </div>
                  </div>

                  <div className="rounded-lg border p-4 space-y-2">
                    <p className="text-sm font-medium">Performance Ranking</p>
                    <div className="flex items-center gap-2">
                      <Progress value={65} className="h-2 flex-1" />
                      <span className="text-sm font-semibold">Top 35%</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Compared to {costCentre.type || 'similar'} cost centres in your organization
                    </p>
                  </div>
                </div>

                <Separator />

                {/* Recommendations */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Actionable Recommendations
                  </h3>
                  
                  <div className="space-y-3">
                    {costPercentUsed >= 90 && (
                      <div className="rounded-lg border p-4 space-y-2">
                        <p className="font-medium">1. Review and Optimize Spending</p>
                        <p className="text-sm text-muted-foreground">
                          Conduct a detailed review of all expenses. Identify non-essential costs that can be deferred or eliminated.
                        </p>
                      </div>
                    )}
                    
                    <div className="rounded-lg border p-4 space-y-2">
                      <p className="font-medium">{costPercentUsed >= 90 ? '2' : '1'}. Improve Cost Tracking</p>
                      <p className="text-sm text-muted-foreground">
                        Implement weekly cost reviews to catch budget issues earlier. Set up automated alerts for unusual spending patterns.
                      </p>
                    </div>

                    <div className="rounded-lg border p-4 space-y-2">
                      <p className="font-medium">{costPercentUsed >= 90 ? '3' : '2'}. Optimize Resource Allocation</p>
                      <p className="text-sm text-muted-foreground">
                        Review team utilization rates. Consider reallocating resources from over-staffed areas to maximize efficiency.
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Trend Analysis */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Trend Analysis
                  </h3>
                  
                  <div className="rounded-lg border p-4 space-y-4">
                    <div>
                      <p className="text-sm font-medium mb-2">Historical Pattern</p>
                      <p className="text-sm text-muted-foreground">
                        Spending has been {costPercentUsed > 80 ? 'accelerating' : 'steady'} over the past 3 months.
                        Current trajectory suggests {costPercentUsed > 90 ? 'budget overrun' : 'completion within budget'}.
                      </p>
                    </div>
                    
                    <Separator />
                    
                    <div>
                      <p className="text-sm font-medium mb-2">Projection</p>
                      <p className="text-sm text-muted-foreground">
                        At current burn rate of {formatCurrency(burnRate)}/month, estimated final cost will be{' '}
                        <span className="font-semibold">
                          {formatCurrency(costCentre.actualCosts + (burnRate * (monthsElapsed * 0.5)))}
                        </span>
                        {' '}({formatPercent((costCentre.actualCosts + (burnRate * (monthsElapsed * 0.5))) / costCentre.costsBudget * 100, 0)} of budget).
                      </p>
                    </div>
                  </div>
                </div>
                </TabsContent>
              </ScrollAreaViewport>
              <ScrollAreaScrollbar orientation="vertical" />
            </ScrollArea>
          </Tabs>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialogs */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteDialogType === 'has-transactions' && 'Cannot Delete Cost Centre'}
              {deleteDialogType === 'has-budget' && 'Delete Cost Centre with Budget?'}
              {deleteDialogType === 'confirm' && 'Delete Cost Centre?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteDialogType === 'has-transactions' && (
                <>
                  Cannot delete cost centre with posted transactions:
                  {costCentre.actualCosts > 0 && <div>• Actual Costs: {formatCurrency(costCentre.actualCosts)}</div>}
                  {costCentre.actualHours > 0 && <div>• Actual Hours: {formatHours(costCentre.actualHours)}</div>}
                  {costCentre.actualRevenue > 0 && <div>• Actual Revenue: {formatCurrency(costCentre.actualRevenue)}</div>}
                  <div className="mt-2">Cost centre can be marked as inactive instead.</div>
                </>
              )}
              {deleteDialogType === 'has-budget' && (
                <>
                  This cost centre has budgets assigned:
                  {costCentre.costsBudget > 0 && <div>• Cost Budget: {formatCurrency(costCentre.costsBudget)}</div>}
                  {costCentre.hoursBudget > 0 && <div>• Hours Budget: {formatHours(costCentre.hoursBudget)}</div>}
                  {costCentre.revenueBudget > 0 && <div>• Revenue Budget: {formatCurrency(costCentre.revenueBudget)}</div>}
                  <div className="mt-2">Are you sure you want to delete it?</div>
                </>
              )}
              {deleteDialogType === 'confirm' && (
                <>
                  Delete cost centre "{costCentre.name}"? This action cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {deleteDialogType === 'has-transactions' ? (
              <>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleMarkInactive}>
                  Mark as Inactive
                </AlertDialogAction>
              </>
            ) : (
              <>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteConfirm}
                  className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
                >
                  {deleteDialogType === 'has-budget' ? 'Delete Anyway' : 'Delete'}
                </AlertDialogAction>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
